import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'components/Button';
import { DataTable, DataTableMessageRow } from 'components/DataTable';
import { Modal } from 'components/Modal';
import { EMPTY_VALUE, MEMO_PREVIEW_LENGTH } from 'constants/index';
import { routes } from 'constants/routes';
import { commonStrings, lineStrings } from 'constants/strings';
import { useOrgPath } from 'hooks/useOrgPath';
import { type TLineChannelAccount } from 'types';
import {
  getStatusLabel,
  getWebhookStatusLabel,
  getWebhookStatusStyle,
} from './lineAccountView';

const buildLineEditPath = (lineChannelId: number) =>
  routes.LINE_EDIT.replace(':lineChannelId', String(lineChannelId));

const normalizeMemo = (memo: string | null) => {
  if (typeof memo !== 'string') {
    return null;
  }
  const trimmed = memo.trim();
  return trimmed ? trimmed : null;
};

const buildMemoPreview = (memo: string | null) => {
  const normalizedMemo = normalizeMemo(memo);
  if (!normalizedMemo) {
    return {
      fullMemo: null,
      preview: EMPTY_VALUE,
      isTruncated: false,
    };
  }

  if (normalizedMemo.length <= MEMO_PREVIEW_LENGTH) {
    return {
      fullMemo: normalizedMemo,
      preview: normalizedMemo,
      isTruncated: false,
    };
  }

  return {
    fullMemo: normalizedMemo,
    preview: `${normalizedMemo.slice(0, MEMO_PREVIEW_LENGTH)}...`,
    isTruncated: true,
  };
};

// ラベル/スタイルのヘルパーは共有モジュール lineAccountView に移し、ここから再エクスポートする
// （テストで本コンポーネントをモックしても他所の import が壊れないようにするため）
export { getStatusLabel, getWebhookStatusLabel, getWebhookStatusStyle };

type LineAccountsTableProps = {
  accounts: TLineChannelAccount[];
  loading: boolean;
  emptyMessage?: string;
  onRequestVerify: (account: TLineChannelAccount) => void;
  onRequestUnlink: (account: TLineChannelAccount) => void;
  isVerifying: boolean;
  unlinkingLineChannelId?: number;
  selectedIds: Set<number>;
  onToggleRow: (lineChannelId: number) => void;
  onToggleAll: (checked: boolean) => void;
  isBulkProcessing: boolean;
};

export function LineAccountsTable({
  accounts,
  loading,
  emptyMessage = lineStrings.management.states.empty,
  onRequestVerify,
  onRequestUnlink,
  isVerifying,
  unlinkingLineChannelId,
  selectedIds,
  onToggleRow,
  onToggleAll,
  isBulkProcessing,
}: LineAccountsTableProps) {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const selectedCount = accounts.reduce(
    (count, account) => (selectedIds.has(account.id) ? count + 1 : count),
    0,
  );
  const allSelected = accounts.length > 0 && selectedCount === accounts.length;
  const someSelected = selectedCount > 0 && !allSelected;

  const [memoModalState, setMemoModalState] = useState<{
    basicId: string;
    memo: string;
  } | null>(null);
  const handleEdit = (lineChannelId: number) =>
    navigate(orgPathFor(buildLineEditPath(lineChannelId)));
  const handleOpenMemo = (account: TLineChannelAccount) => {
    const memo = normalizeMemo(account.memo);
    if (!memo) {
      return;
    }

    setMemoModalState({
      basicId: account.basicId,
      memo,
    });
  };
  let body: JSX.Element | JSX.Element[];

  if (loading) {
    body = <DataTableMessageRow colSpan={12} message={commonStrings.loading} />;
  } else if (accounts.length === 0) {
    body = (
      <DataTableMessageRow
        colSpan={12}
        message={emptyMessage}
      />
    );
  } else {
    body = accounts.map((account) => (
      <LineAccountsTableRow
        key={account.id}
        account={account}
        onEdit={handleEdit}
        onRequestVerify={onRequestVerify}
        onOpenMemo={handleOpenMemo}
        onRequestUnlink={onRequestUnlink}
        isVerifying={isVerifying}
        isUnlinking={unlinkingLineChannelId === account.id}
        isSelected={selectedIds.has(account.id)}
        onToggleRow={onToggleRow}
        isBulkProcessing={isBulkProcessing}
      />
    ));
  }

  return (
    <>
      <DataTable
        minWidthClassName="min-w-[1308px]"
        headerRow={
          <tr>
            <th className="w-[48px] px-2 py-3 text-center leading-tight whitespace-nowrap sticky left-0 z-10 bg-surface/70">
              <input
                ref={(element) => {
                  if (element) {
                    element.indeterminate = someSelected;
                  }
                }}
                type="checkbox"
                className="h-4 w-4 cursor-pointer rounded border-border/80 text-accent focus:ring-accent/30 disabled:cursor-not-allowed"
                aria-label={lineStrings.management.bulk.selectAllLabel}
                checked={allSelected}
                disabled={isBulkProcessing || accounts.length === 0}
                onChange={(event) => onToggleAll(event.target.checked)}
              />
            </th>
            <th className="w-[88px] px-2 py-3 text-left leading-tight whitespace-nowrap sticky left-[48px] z-10 bg-surface/70">
              {lineStrings.management.table.headers.actions}
            </th>
            <th className="w-[64px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.id}
            </th>
            <th className="w-[150px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.channelId}
            </th>
            <th className="w-[120px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.basicId}
            </th>
            <th className="w-[110px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.friendLimit}
            </th>
            <th className="w-[96px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.activeFriends}
            </th>
            <th className="w-[148px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.status}
            </th>
            <th className="w-[120px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.webhookStatus}
            </th>
            <th className="w-[112px] px-2 py-3 text-center leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.webhookVerify}
            </th>
            <th className="w-[260px] px-2 py-3 text-left leading-tight whitespace-nowrap">
              {lineStrings.management.table.headers.memo}
            </th>
            <th className="w-[112px] px-2 py-3 text-center leading-tight whitespace-nowrap">
              {lineStrings.management.actions.unlink}
            </th>
          </tr>
        }
      >
        {body}
      </DataTable>
      <Modal open={Boolean(memoModalState)} onClose={() => setMemoModalState(null)}>
        <div className="grid gap-4">
          <h2 className="text-lg font-semibold text-ink">
            @{memoModalState?.basicId} {lineStrings.management.table.headers.memo}
          </h2>
          <p className="max-h-[55vh] overflow-y-auto whitespace-pre-wrap break-words text-center text-sm text-ink">
            {memoModalState?.memo}
          </p>
          <div className="flex justify-end">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setMemoModalState(null)}
            >
              {commonStrings.cancel}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

type LineAccountsTableRowProps = {
  account: TLineChannelAccount;
  onEdit: (lineChannelId: number) => void;
  onRequestVerify: (account: TLineChannelAccount) => void;
  onOpenMemo: (account: TLineChannelAccount) => void;
  onRequestUnlink: (account: TLineChannelAccount) => void;
  isVerifying: boolean;
  isUnlinking: boolean;
  isSelected: boolean;
  onToggleRow: (lineChannelId: number) => void;
  isBulkProcessing: boolean;
};

function LineAccountsTableRow({
  account,
  onEdit,
  onRequestVerify,
  onOpenMemo,
  onRequestUnlink,
  isVerifying,
  isUnlinking,
  isSelected,
  onToggleRow,
  isBulkProcessing,
}: LineAccountsTableRowProps) {
  const basicId = `@${account.basicId}`;
  const statusLabel = getStatusLabel(account.status);
  const memo = buildMemoPreview(account.memo);

  return (
    <tr
      className={`border-t border-border/60 transition-colors ${
        isSelected ? 'bg-accent-soft/60' : 'bg-white'
      }`}
    >
      <td
        className={`px-2 py-3 text-center sticky left-0 z-10 ${
          isSelected ? 'bg-accent-soft/60' : 'bg-white'
        }`}
      >
        <input
          type="checkbox"
          className="h-4 w-4 cursor-pointer rounded border-border/80 text-accent focus:ring-accent/30 disabled:cursor-not-allowed"
          aria-label={`${lineStrings.management.bulk.selectRowLabel} ${basicId}`}
          checked={isSelected}
          disabled={isBulkProcessing}
          onChange={() => onToggleRow(account.id)}
        />
      </td>
      <td
        className={`px-2 py-3 text-left sticky left-[48px] z-10 shadow-[2px_0_4px_rgba(0,0,0,0.05)] ${
          isSelected ? 'bg-accent-soft/60' : 'bg-white'
        }`}
      >
        <Button
          type="button"
          size="sm"
          onClick={() => onEdit(account.id)}
        >
          {commonStrings.actions.edit}
        </Button>
      </td>
      <td className="px-2 py-3 text-[11px] font-semibold text-ink sm:text-sm">
        {account.id}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {account.channelId}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">{basicId}</td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {account.friendLimit}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {account.activeFriends}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        {statusLabel}
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${getWebhookStatusStyle(account.webhookStatus)}`}>
          {getWebhookStatusLabel(account.webhookStatus)}
        </span>
      </td>
      <td className="px-2 py-3 text-center">
        <Button
          type="button"
          size="sm"
          disabled={isVerifying || isUnlinking || isBulkProcessing}
          onClick={() => onRequestVerify(account)}
        >
          {lineStrings.management.actions.verify}
        </Button>
      </td>
      <td className="px-2 py-3 text-[11px] text-ink sm:text-sm">
        <div className="flex items-center gap-2">
          <span title={memo.fullMemo ?? undefined}>{memo.preview}</span>
          {memo.isTruncated && memo.fullMemo && (
            <button
              type="button"
              className="text-[11px] font-semibold text-accent underline decoration-accent/60 underline-offset-2 hover:text-accent/80"
              onClick={() => onOpenMemo(account)}
            >
              {lineStrings.management.actions.viewMemo}
            </button>
          )}
        </div>
      </td>
      <td className="px-2 py-3 text-right">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="border-[#F35D5D] bg-[#F35D5D] text-white shadow-[0_16px_30px_rgba(243,93,93,0.32)] hover:border-[#E34E4E] hover:bg-[#E34E4E] hover:text-white hover:shadow-[0_20px_40px_rgba(243,93,93,0.38)] disabled:shadow-none"
          disabled={isVerifying || isUnlinking || isBulkProcessing}
          onClick={() => onRequestUnlink(account)}
        >
          {lineStrings.management.actions.unlink}
        </Button>
      </td>
    </tr>
  );
}
